/* Atlas.tsx — the plant map.
 *
 * The canvas itself is `atlas/engine.ts`, ported verbatim. This file is everything AROUND it: the
 * filter rail, the stat strip, the funnel, the worklist and the plant panel.
 *
 * ONE STRUCTURAL CHANGE, and it fixes a real bug rather than restyling anything. The old app kept
 * the chosen SETTINGS and the loaded DATASET on the same object and persisted it with
 * `Object.assign(prefs.map, MAP)`. `rows` alone is 33,710 plants and 7.79 MB of JSON, localStorage
 * is about 5 MB, so setItem threw QuotaExceededError into an empty catch — and from the first touch
 * of any Atlas control onward EVERY preference in the app silently stopped saving. It needed a
 * hand-maintained deny-list to stay fixed. Here the settings are React state and the dataset lives
 * in the engine's MAP, so there is nothing to deny-list: a new data payload cannot reach
 * localStorage because it is not in the object that gets written.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, isErr, type Meta } from '../lib/api.ts';
import { N, N1, SQ, pct, haversine, PLACED_NOTE, LVLNAME, ROLE_LABEL, ROLE_MEANS } from '../lib/fmt.ts';
import { Btn, Chip, Collapsible, ErrBox, Hint, Meter, Section, Seg, Stat, HAIR, selStyle } from '../components/ui.tsx';
import { Matrix, PeopleList, type Cell } from '../components/data.tsx';
import * as E from './atlas/engine.ts';

const LS = 'atlas-map-v1';

interface GmrOpt { code: string; name: string | null; n: number; l1?: string | null }
interface GmrMeta { l1: GmrOpt[]; l2: GmrOpt[]; actions: GmrOpt[]; counts: Record<string, number> }

/* SETTINGS ONLY. Nothing here is a payload, which is what makes persisting it safe. */
interface Filters {
  q: string[]; state: string; tier: string;
  minsq: number; reachmin: number; empmin: number; empmax: number; revmin: number;
  roles: string[]; show: 'reach' | 'full' | 'all';
  gl1: string[]; gl2: string[]; gact: string[]; gtagged: boolean;
  radius: number;
}
interface ViewPrefs {
  mode: 'committee' | 'reach' | 'sqft' | 'tier';
  density: boolean; showLinks: boolean; showOrg: boolean; flip: boolean; paneClosed: boolean;
  open: Record<string, boolean>;
}

const F0: Filters = {
  q: [], state: '', tier: '', minsq: 0, reachmin: 0, empmin: 0, empmax: 0, revmin: 0,
  roles: [], show: 'reach', gl1: [], gl2: [], gact: [], gtagged: false, radius: 0,
};
const V0: ViewPrefs = {
  mode: 'committee', density: true, showLinks: false, showOrg: false, flip: false,
  paneClosed: false, open: {},
};

const EMP_BANDS = [50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
const REV_BANDS = [1e6, 1e7, 5e7, 1e8, 5e8, 1e9, 5e9, 1e10, 5e10];
const ACTION_CAP = 21;   // The agreed cut: lamination and above by count; the smaller ones are dropped.

/* The filter the server compiles. Every control on the rail is one clause here, so the Atlas asks
   the same question the grid does and gets the same answer. */
function mapFilter(f: Filters, pin: { f: string; o: string; v: string; label: string } | null) {
  const kids: unknown[] = [{ f: 'lat', o: 'nempty' }];
  // A selection sent over from the grid. It is a normal condition, not a special mode, so every
  // other control still narrows inside it — and the chip at the top of the rail says it is on with
  // an × to clear it: a map silently showing 12 plants out of 33,710 is the condition this chip
  // exists to make visible. Never persisted.
  if (pin) kids.push({ f: pin.f, o: pin.o, v: pin.v });
  if (f.tier) kids.push({ f: 'tier', o: 'eq', v: f.tier });
  /* Search terms OR together, and each term looks in the FOUR places a rep would look: the site
     name, the company that owns it, the TOWN, and the DOMAIN.
     Domain was missing, and it is the thing a rep actually types. Searching a domain returned
     nothing because the search read names only, which left guessing a plant name as the way round
     it — and plant names here are not guessable. Texas Pipe and GE Vernova both failed that way. */
  const terms = f.q.filter(Boolean);
  if (terms.length) {
    const one = (t: string) => ({ op: 'OR', kids: [
      { f: 'site', o: 'contains', v: t }, { f: 'company', o: 'contains', v: t },
      { f: 'city', o: 'contains', v: t }, { f: 'domain', o: 'contains', v: t },
    ] });
    kids.push(terms.length === 1 ? one(terms[0]) : { op: 'OR', kids: terms.map(one) });
  }
  if (f.state) kids.push({ f: 'state', o: 'eq', v: f.state });
  // Headcount and revenue are carried down from the ACCOUNT. The control says "whole company"
  // because that is what the number is — it is not a per-plant count.
  if (f.empmin) kids.push({ f: 'gmr_employees', o: 'gte', v: f.empmin });
  if (f.empmax) kids.push({ f: 'gmr_employees', o: 'lte', v: f.empmax });
  if (f.revmin) kids.push({ f: 'gmr_revenue', o: 'gte', v: f.revmin });
  if (f.reachmin) kids.push({ f: 'reach', o: 'gte', v: f.reachmin });
  // Which buying roles must actually be reachable AT THE BUILDING. This is the question the whole
  // product is about, and it had no control on the map at all before.
  for (const r of f.roles) kids.push({ f: r, o: 'gte', v: 1 });
  // 19,507 of 33,512 geocoded plants have nobody reachable. They were the bulk of the clutter, so
  // they are OFF by default and the control that reveals them states its own count — a map that
  // silently hides 58% of the footprint would misrepresent the footprint.
  if (f.show !== 'all') kids.push({ f: 'reach', o: 'gte', v: 1 });
  if (f.show === 'full') kids.push({ f: 'committee', o: 'eq', v: 4 });
  if (f.minsq) kids.push({ f: 'sqft', o: 'gte', v: f.minsq });
  // From the client's own files. Exact membership against the pipe-delimited key, never a substring of
  // the display string. Several picks of the same kind are an OR — "aerospace or marine" — while
  // different kinds still AND together, which is how the question is actually meant.
  for (const [kind, key] of [['l1', 'gl1'], ['l2', 'gl2'], ['action', 'gact']] as const) {
    const list = f[key].filter(Boolean);
    if (list.length === 1) kids.push({ gmr: kind, v: list[0] });
    else if (list.length > 1) kids.push({ op: 'OR', kids: list.map((x) => ({ gmr: kind, v: x })) });
  }
  if (f.gtagged) kids.push({ tag: 'no finishing actions', not: true });
  return { op: 'AND', kids };
}

const load = <T,>(k: string, d: T): T => {
  try { return { ...d, ...(JSON.parse(localStorage.getItem(k) || '{}') || {}) }; } catch { return d; }
};

export function Atlas({ meta, pin, onClearPin, onOpenPerson }: {
  meta: Meta;
  pin?: { f: string; o: string; v: string; label: string } | null;
  onClearPin?: () => void;
  onOpenPerson?: (id: number) => void;
}) {
  const [f, setF] = useState<Filters>(() => load(LS + '-f', F0));
  const [v, setV] = useState<ViewPrefs>(() => load(LS + '-v', V0));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [gen, setGen] = useState(0);            // bumped when the engine has new data to draw from
  const [sel, setSel] = useState<number | null>(null);
  const [region, setRegion] = useState<E.MapRow[] | null>(null);
  const [tip, setTip] = useState<{ x: number; y: number; r: E.MapRow } | null>(null);
  const [linkNote, setLinkNote] = useState('');
  const [orgNote, setOrgNote] = useState('');

  const cvRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { localStorage.setItem(LS + '-f', JSON.stringify(f)); }, [f]);
  useEffect(() => { localStorage.setItem(LS + '-v', JSON.stringify(v)); }, [v]);

  const set = <K extends keyof Filters>(k: K, val: Filters[K]) => setF((p) => ({ ...p, [k]: val }));
  const setView = <K extends keyof ViewPrefs>(k: K, val: ViewPrefs[K]) => setV((p) => ({ ...p, [k]: val }));

  const filter = useMemo(() => JSON.stringify(mapFilter(f, pin ?? null)), [f, pin]);

  /* Push the view preferences into the engine. They are drawing inputs, not data, so this is a
     cheap assign plus a repaint — MAP.ver is untouched and the point arrays are not rebuilt. */
  useEffect(() => {
    E.MAP.mode = v.mode; E.MAP.density = v.density;
    E.MAP.showLinks = v.showLinks; E.MAP.showOrg = v.showOrg; E.MAP.flip = v.flip;
    /* Both of these are ZERO here, and that is the point of moving them out of the engine.
       In the old app the filter rail and the stat strip FLOATED OVER the canvas, so fit(), the
       label passes and the legend all had to reserve the space they covered. In this layout they
       are sibling flex columns: the canvas begins where the rail ends and ends where the stats
       begin, so reserving that space again indents the legend and the place names 264px into the
       middle of the map. `statH` keeps a small pad because the legend and the zoom caption are
       drawn INSIDE the canvas, at its foot. */
    E.MAP.railW = 0; E.MAP.statH = 10;
    E.draw();
  }, [v, gen]);

  useEffect(() => { E.MAP.radius = f.radius; E.draw(); }, [f.radius]);

  // ── the map itself ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let dead = false;
    setBusy(true); setErr(null);
    api<Parameters<typeof E.setPoints>[0]>('map?q=' + encodeURIComponent(filter)).then((r) => {
      if (dead) return;
      setBusy(false);
      if (isErr(r)) {
        // The previous frame must not stand as if it were the answer — same treatment the grid
        // gives a rejected filter.
        setErr(r.error); return;
      }
      E.MAP.showDark = f.show === 'all'; E.MAP.full = f.show === 'full';
      E.setPoints(r);
      E.sizeCanvas();
      if (!E.MAP.fitted) { E.fit(); E.MAP.fitted = true; }
      E.draw();
      setGen((g) => g + 1);
    });
    return () => { dead = true; };
  }, [filter, f.show]);

  // Group links follow the SAME filter as the plants, so they track every rail change.
  useEffect(() => {
    if (!v.showLinks) { E.MAP.links = null; E.MAP.linkVer++; setLinkNote(''); E.draw(); return; }
    let dead = false;
    api<{ links: E.GroupLink[]; total: number }>('grouplinks?q=' + encodeURIComponent(filter)).then((r) => {
      if (dead || isErr(r)) return;
      E.MAP.links = r.links; E.MAP.linkVer++;
      // The layer STATES what it is showing: a map drawing 4 lines out of 1,247 and saying nothing
      // is indistinguishable from a broken layer.
      const shown = E.linksToDraw().length;
      setLinkNote(sel ? `${N(shown)} for this company`
        : E.MAP.scale < E.LINK_ZOOM_LEVEL ? `${N(r.links.length)} — zoom in, or click a plant`
          : `biggest ${N(shown)} of ${N(r.links.length)} — click a plant for one group`);
      E.draw();
    });
    return () => { dead = true; };
  }, [v.showLinks, filter, sel, gen]);

  // The org overlay follows the selected company.
  useEffect(() => {
    if (!v.showOrg) { E.MAP.org = null; setOrgNote(''); E.draw(); return; }
    const r = sel != null ? E.MAP.byId[sel] : null;
    if (!r) { E.MAP.org = null; setOrgNote('click a plant'); E.draw(); return; }
    let dead = false;
    api<E.OrgMap>('orgmap?domain=' + encodeURIComponent(r.domain || '')).then((o) => {
      if (dead) return;
      E.MAP.org = isErr(o) ? null : o;
      setOrgNote(!E.MAP.org ? 'none' : E.MAP.org.note ? 'no buyer'
        : `${N(E.MAP.org.plants.length)}${E.MAP.org.ungeocoded ? ` · ${N(E.MAP.org.ungeocoded)} off map` : ''}`);
      E.draw();
    });
    return () => { dead = true; };
  }, [v.showOrg, sel, gen]);

  // Canvas lifecycle. The rAF loop runs ONLY while this screen is mounted.
  useEffect(() => {
    E.attach(cvRef.current);
    E.loadGeo();
    E.sizeCanvas();
    E.startPulse();
    const onResize = () => E.sizeCanvas();
    addEventListener('resize', onResize);
    return () => { removeEventListener('resize', onResize); E.stopPulse(); E.attach(null); E.freeAtlas(); };
  }, []);

  useEffect(() => { E.sizeCanvas(); }, [v.paneClosed, v.flip]);
  useEffect(() => { E.MAP.sel = sel; E.draw(); }, [sel]);

  // ── pointer ───────────────────────────────────────────────────────────────────────────────────
  const pos = (e: React.MouseEvent) => {
    const r = (e.target as HTMLCanvasElement).getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top] as const;
  };
  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const r = cvRef.current!.getBoundingClientRect();
    E.zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * 0.0012));
  }, []);
  useEffect(() => {
    const cv = cvRef.current; if (!cv) return;
    // Non-passive: a passive wheel listener cannot preventDefault, so the page scrolls while the map
    // zooms. React's onWheel is passive by default, which is why this is wired by hand.
    cv.addEventListener('wheel', onWheel, { passive: false });
    return () => cv.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  const onDown = (e: React.MouseEvent) => {
    const [x, y] = pos(e);
    E.MAP.drag = { x, y, cx: E.MAP.cx, cy: E.MAP.cy, shift: e.shiftKey };
    if (e.shiftKey) E.MAP.box = { x0: x, y0: y, x1: x, y1: y };
  };
  const onMove = (e: React.MouseEvent) => {
    const [x, y] = pos(e);
    const d = E.MAP.drag;
    if (d && d.shift) { E.MAP.box = { x0: d.x, y0: d.y, x1: x, y1: y }; E.draw(); return; }
    if (d) { E.MAP.cx = d.cx - (x - d.x) / E.MAP.scale; E.MAP.cy = d.cy + (y - d.y) / E.MAP.scale; E.draw(); return; }
    const p = E.nearest(x, y);
    const r = p ? E.MAP.byId[p[0]] : null;
    setTip(r ? { x, y, r } : null);
  };
  const onUp = (e: React.MouseEvent) => {
    const [x, y] = pos(e);
    const d = E.MAP.drag;
    const wasBox = d && d.shift;
    const moved = d && (Math.abs(x - d.x) + Math.abs(y - d.y)) > 4;
    E.MAP.drag = null;
    if (wasBox) {
      const b = E.MAP.box; E.MAP.box = null;
      if (b && Math.abs(b.x1 - b.x0) >= 6) { setRegion(E.inBox(b)); setSel(null); }
      E.draw(); return;
    }
    if (!moved) { const p = E.nearest(x, y); if (p) { setSel(p[0]); setRegion(null); } }
    else E.draw();     // drag over: rebuild the static layer at the position we panned to
  };

  // ── rail ──────────────────────────────────────────────────────────────────────────────────────
  const gmr = meta.gmr as GmrMeta | null;
  const liveCount = {
    size: (['minsq', 'reachmin', 'empmin', 'empmax', 'revmin'] as const).filter((k) => f[k]).length,
    comm: f.roles.length,
    acct: (['tier', 'state'] as const).filter((k) => f[k]).length,
    gmr: f.gl1.length + f.gl2.length + f.gact.length + (f.gtagged ? 1 : 0),
    trip: f.radius ? 1 : 0,                       // a halo, it filters nothing — excluded from the total
  };
  const liveTotal = liveCount.size + liveCount.comm + liveCount.acct + liveCount.gmr
    + f.q.length + (pin ? 1 : 0);

  const secOpen = (k: string) => v.open[k] !== false;   // open on first visit, collapsed only once chosen
  const toggleSec = (k: string) => setView('open', { ...v.open, [k]: !secOpen(k) });

  const rows = E.MAP.rows;
  const inView = rows.length || 1;
  const totalSq = rows.reduce((a, b) => a + (b.sqft || 0), 0);
  const totalReach = rows.reduce((a, b) => a + b.reach, 0);
  const full = rows.filter((x) => x.committee === 4).length;
  const dark = rows.filter((x) => !x.reach).length;
  const topSt = E.MAP.byState[0];

  return (
    <div style={{ display: 'flex', flexDirection: v.flip ? 'row-reverse' : 'row', height: '100%', minHeight: 0 }}>

      {/* ── filter rail ─────────────────────────────────────────────────────────────────────── */}
      <aside style={{ width: 264, flex: '0 0 264px', borderRight: HAIR, overflowY: 'auto' }}>
        <Section label="Find">
          <input
            placeholder="company, domain, site or town — no AI, no cost"
            style={selStyle}
            onKeyDown={(e) => {
              const el = e.currentTarget;
              if (e.key === 'Enter') { e.preventDefault(); const t = el.value.trim(); if (t) { set('q', [...new Set([...f.q, t])]); el.value = ''; } }
              else if (e.key === 'Backspace' && !el.value && f.q.length) set('q', f.q.slice(0, -1));
              else if (e.key === 'Escape') { el.value = ''; el.blur(); }
            }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
            {f.q.map((t, i) => (
              <button key={t} onClick={() => set('q', f.q.filter((_, j) => j !== i))} title="remove"
                style={{ border: HAIR, background: 'transparent', color: 'var(--dim)', cursor: 'pointer', padding: '1px 5px', fontSize: 'var(--t-micro)', fontFamily: 'var(--sans)' }}>
                {t} ×
              </button>
            ))}
          </div>
          {/* The grid hand-off chip. pickToAtlas always wrote a label; until it was rendered the map
              silently showed a handful of plants with no visible reason and no way off it. */}
          {pin && (
            <div style={{ marginTop: 7 }}>
              <Chip tone="warn" title="Sent over from the grid. Every other filter on this rail still narrows inside it.">
                showing only {pin.label}
                <button onClick={onClearPin} title="Clear this and show every plant again"
                  style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', padding: 0 }}>×</button>
              </Chip>
            </div>
          )}
        </Section>

        {/* THE PRIMARY CONTROL. Everything under it is granularity; this is the question a person
            opening the Atlas is actually asking. It used to be two scattered checkboxes with
            overlapping meaning — "sellable today" already implies someone is reachable, so a state
            where both were ticked said one thing and meant another. */}
        <Section label="Show me">
          {([
            ['reach', 'Plants with someone reachable', 'the working set'],
            ['full', 'Sellable today', 'all four buying roles reachable in the building'],
            ['all', 'Every geocoded plant', `includes buildings with nobody reachable${E.MAP.darkTotal != null ? ` (${N(E.MAP.darkTotal)})` : ''}`],
          ] as const).map(([id, title, sub]) => (
            <Btn key={id} wide on={f.show === id} onClick={() => set('show', id)} style={{ marginBottom: 3, display: 'block' }}>
              <div style={{ fontWeight: 600 }}>{title}</div>
              <div className="t-micro" style={{ color: 'var(--dim2)' }}>{sub}</div>
            </Btn>
          ))}
        </Section>

        <Section label="Colour the map by">
          <Seg wide value={v.mode} onPick={(m) => setView('mode', m)}
            options={[['committee', 'Committee'], ['reach', 'Reachable'], ['sqft', 'Sq ft'], ['tier', 'Tier']]} />
          <div className="t-label" style={{ margin: '12px 0 5px' }}>Overlays</div>
          <Toggle on={v.density} onChange={(x) => setView('density', x)} label="density field" />
          <Toggle on={v.showLinks} onChange={(x) => setView('showLinks', x)}
            label="corporate groups" note={linkNote} noteColor="var(--vi)"
            title="Draws a line from a parent company's biggest plant to each of its subsidiaries. Display only: it changes no count." />
          <Toggle on={v.showOrg} onChange={(x) => setView('showOrg', x)}
            label="org structure" note={orgNote} noteColor="var(--or2)"
            title="Click a plant first. Draws that company's whole footprint and marks the building holding the most senior person you can reach. Seniority is read off job titles, not a reporting line anyone gave us. Display only: it changes no count." />
        </Section>

        <Collapsible label="Size & reach" live={liveCount.size} open={secOpen('size')} onToggle={() => toggleSec('size')}>
          <Slider label="Min sq ft" value={f.minsq} max={2_000_000} step={50_000}
            fmt={(x) => (x ? SQ(x) : 'any')} onChange={(x) => set('minsq', x)} />
          <Slider label="Min reachable buyers here" value={f.reachmin} max={200} step={5}
            fmt={(x) => (x ? String(x) : 'any')} onChange={(x) => set('reachmin', x)} />
          <div className="t-label" style={{ margin: '10px 0 4px' }}>
            Company headcount <span className="num" style={{ color: 'var(--or2)' }}>
              {f.empmin || f.empmax ? `${f.empmin ? N(f.empmin) : '0'} – ${f.empmax ? N(f.empmax) : '∞'}` : 'any'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 5 }}>
            <select style={selStyle} value={f.empmin} onChange={(e) => set('empmin', +e.target.value)}>
              <option value={0}>no minimum</option>
              {EMP_BANDS.map((b) => <option key={b} value={b}>{N(b)}+</option>)}
            </select>
            <select style={selStyle} value={f.empmax} onChange={(e) => set('empmax', +e.target.value)}>
              <option value={0}>no maximum</option>
              {EMP_BANDS.map((b) => <option key={b} value={b}>under {N(b)}</option>)}
            </select>
          </div>
          <div className="t-label" style={{ margin: '10px 0 4px' }}>
            Min company revenue <span className="num" style={{ color: 'var(--or2)' }}>{f.revmin ? SQ(f.revmin) + '+' : 'any'}</span>
          </div>
          <select style={selStyle} value={f.revmin} onChange={(e) => set('revmin', +e.target.value)}>
            <option value={0}>any</option>
            {REV_BANDS.map((b) => <option key={b} value={b}>{SQ(b)}+</option>)}
          </select>
          <Hint style={{ marginTop: 6 }}>Headcount and revenue are company-wide, from your SOM sheet. Not per plant.</Hint>
        </Collapsible>

        <Collapsible label="Buying committee" live={liveCount.comm} open={secOpen('comm')} onToggle={() => toggleSec('comm')}>
          <Hint style={{ marginBottom: 5 }}>
            Which roles must be reachable IN this building. For all four at once, use "Sellable today" above.
          </Hint>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {([['dm', 'decision_maker'], ['sp', 'sponsor'], ['inf', 'influencer'], ['usr', 'user']] as const).map(([k, r]) => (
              <Btn key={k} sm on={f.roles.includes(k)} title={ROLE_MEANS[r]}
                onClick={() => set('roles', f.roles.includes(k) ? f.roles.filter((x) => x !== k) : [...f.roles, k])}>{ROLE_LABEL[r]}</Btn>
            ))}
          </div>
        </Collapsible>

        <Collapsible label="Account" live={liveCount.acct} open={secOpen('acct')} onToggle={() => toggleSec('acct')}>
          <select style={{ ...selStyle, marginBottom: 5 }} value={f.tier} onChange={(e) => set('tier', e.target.value)}>
            <option value="">all tiers</option>
            {['1', '2', '3', '4'].map((t) => <option key={t} value={t}>T{t}</option>)}
          </select>
          {/* Built from meta, which is the WHOLE dataset — never from the map response, which is
              already filtered. A picker rebuilt from a filtered response would contain only the
              state you just picked, with no way back to the whole map. */}
          <select style={selStyle} value={f.state} onChange={(e) => set('state', e.target.value)}>
            <option value="">all states</option>
            {(meta.states || []).map((s) => <option key={s.state} value={s.state}>{s.state} ({N(s.plants)})</option>)}
          </select>
        </Collapsible>

        <Collapsible label="Industry & actions" live={liveCount.gmr} open={secOpen('gmr')} onToggle={() => toggleSec('gmr')}>
          {!gmr ? (
            <Hint>Your industry and finishing-action files are not loaded into this snapshot. They come from
              the tagging dump and the SOM sheet, and they populate <span style={{ fontFamily: 'var(--mono)' }}>serve.company_tag</span>,{' '}
              <span style={{ fontFamily: 'var(--mono)' }}>serve.company_action</span> and the two{' '}
              <span style={{ fontFamily: 'var(--mono)' }}>serve.tag_*</span> tables. If those tables are empty, this
              filter has nothing to offer — check the load rather than this screen.</Hint>
          ) : (
            <>
              <Combo opts={gmr.l1} value={f.gl1} onPick={(x) => set('gl1', x)} placeholder="any vertical — type to search" />
              <Combo opts={gmr.l2} value={f.gl2} onPick={(x) => set('gl2', x)} cap={40} placeholder={`any sub-segment — ${N(gmr.l2.length)} to search`} />
              <Combo opts={gmr.actions} value={f.gact} onPick={(x) => set('gact', x)} cap={ACTION_CAP} placeholder="any finishing action — type to search" />
              <Toggle on={f.gtagged} onChange={(x) => set('gtagged', x)} label="only companies you have tagged" />
              <Hint style={{ marginTop: 6 }}>
                {N(gmr.counts.tag_rows)} sub-segment tags on {N(gmr.counts.tagged_companies)} companies · {N(gmr.counts.action_rows)} action rows.
                Loaded exactly as sent: not validated, not scored.
              </Hint>
            </>
          )}
        </Collapsible>

        <Collapsible label="Trip planning" live={liveCount.trip} open={secOpen('trip')} onToggle={() => toggleSec('trip')}>
          <Slider label="Radius around the plant you click" value={f.radius} max={300} step={10}
            fmt={(x) => (x ? x + ' mi' : 'off')} onChange={(x) => set('radius', x)} />
        </Collapsible>

        {liveTotal > 0 && (
          <div style={{ padding: 'var(--gut)' }}>
            {/* pin included: a "clear filters" that left an invisible grid hand-off filtering the map
                was the exact defect the chip exists to prevent. */}
            <Btn wide onClick={() => { setF(F0); onClearPin?.(); E.MAP.fitted = false; }}
              style={{ textAlign: 'center' }}>clear {liveTotal} filter{liveTotal === 1 ? '' : 's'}</Btn>
          </div>
        )}
        <Hint style={{ padding: '0 var(--gut) var(--gut)' }}>
          drag to pan · wheel to zoom · shift-drag a box for a trip · click a plant
        </Hint>
      </aside>

      {/* ── map column ───────────────────────────────────────────────────────────────────────── */}
      <section ref={wrapRef} style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {err && <ErrBox what="The map did not load" msg={err} />}
        <div style={{ flex: '1 1 auto', minHeight: 0, position: 'relative' }}>
          <canvas
            ref={cvRef}
            style={{ width: '100%', height: '100%', display: 'block', cursor: E.MAP.drag ? 'grabbing' : 'crosshair' }}
            onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp}
            onMouseLeave={() => { setTip(null); E.MAP.drag = null; }}
          />
          {tip && (
            <div style={{
              position: 'absolute', pointerEvents: 'none', zIndex: 3,
              left: Math.min(tip.x + 14, (E.MAP.w || 800) - 290), top: Math.max(6, tip.y - 62),
              background: 'var(--s2)', border: HAIR, padding: '6px 8px', maxWidth: 280,
              fontSize: 'var(--t-micro)', lineHeight: 1.5,
            }}>
              <div style={{ fontWeight: 700, color: 'var(--ink)' }}>{tip.r.site || tip.r.company}</div>
              <div style={{ color: 'var(--dim)' }}>{tip.r.company} · T{tip.r.tier || '?'}</div>
              <div style={{ color: 'var(--dim)' }}>{tip.r.city || ''}, {tip.r.state || ''} · {SQ(tip.r.sqft)} sq ft</div>
              <div>
                <span style={{ color: 'var(--dim)' }}>{N(tip.r.placed)} placed here · </span>
                <span style={{ color: 'var(--ok)' }}>{N(tip.r.reach)} reachable</span>
                <span style={{ color: 'var(--dim)' }}> · committee {tip.r.committee}/4</span>
              </div>
              <div style={{ color: 'var(--dim2)' }}>placed counts the master list — the people lists show reachable only</div>
            </div>
          )}
          {busy && (
            <div className="t-micro" style={{ position: 'absolute', top: 10, right: 12, color: 'var(--dim)' }}>loading the map…</div>
          )}
        </div>

        {/* The stat row. Hairline-separated columns, a share bar ONLY where the share is a real
            fraction of the plants in view. "Nobody reachable" while hidden counts a DIFFERENT set,
            so it gets no bar and is dimmed instead of quietly implying it belongs to this one. */}
        <div style={{ display: 'flex', borderTop: HAIR, flex: '0 0 auto', overflowX: 'auto' }}>
          <Stat label="Plants on map" value={N(rows.length)} sub={f.show === 'all' ? 'every geocoded plant' : 'with someone reachable'} />
          <Stat label="Sq ft" value={SQ(totalSq)} sub="floor space in view" />
          <Stat label="Reachable buyers" value={N(totalReach)} sub="people we can contact" />
          <Stat label="4 of 4 committee" value={N(full)} sub={`${pct(full, inView)} of plants in view`} frac={full / inView} />
          {f.show === 'all'
            ? <Stat label="Nobody reachable" value={N(dark)} sub={`${pct(dark, inView)} of plants in view`} frac={dark / inView} />
            : <Stat label="Nobody reachable" value={N(E.MAP.darkTotal ?? '—')} sub={'hidden · see "Show me"'} dim />}
          <Stat label="Top state" value={topSt ? topSt.state : '—'}
            sub={topSt ? `${N(topSt.plants)} plants, ${pct(topSt.plants, inView)} of the view` : 'most plants in view'}
            frac={topSt ? topSt.plants / inView : null} />
        </div>
      </section>

      {/* ── worklist / plant pane ────────────────────────────────────────────────────────────── */}
      {!v.paneClosed ? (
        <aside style={{ width: 420, flex: '0 0 420px', borderLeft: HAIR, borderRight: v.flip ? HAIR : undefined, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 var(--gut)', height: 34, borderBottom: HAIR, flex: '0 0 auto' }}>
            <span className="t-label">{sel != null ? 'Plant' : region ? 'Region selection' : 'Worklist'}</span>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
              {(sel != null || region) && (
                <Btn sm title="Back to the worklist for the whole map" onClick={() => { setSel(null); setRegion(null); }}>←</Btn>
              )}
              <Btn sm title="Move this pane to the other side" onClick={() => setView('flip', !v.flip)}>⇄</Btn>
              <Btn sm title="Close this pane and give the map the full width" onClick={() => setView('paneClosed', true)}>✕</Btn>
            </div>
          </div>
          <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
            {sel != null
              ? <PlantPanel id={sel} radius={f.radius} onOpenPerson={onOpenPerson} />
              : region
                ? <RegionPanel rows={region} onPick={setSel} />
                : <Worklist rows={rows} funnel={E.MAP.funnel} show={f.show} trunc={E.MAP.trunc} shownPts={E.MAP.pts.length} onPick={setSel} />}
          </div>
        </aside>
      ) : (
        // A pane you can shut and cannot reopen is a trap, so the reopen tab is pinned to the edge
        // it left from and says what it is.
        <button onClick={() => setView('paneClosed', false)} title="Reopen the worklist"
          style={{ alignSelf: 'flex-start', marginTop: 40, border: HAIR, borderRight: 'none', background: 'var(--s2)', color: 'var(--dim)', cursor: 'pointer', padding: '10px 4px', writingMode: 'vertical-rl', fontSize: 'var(--t-micro)' }}>
          ◀ worklist
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────── rail parts

function Toggle({ on, onChange, label, note, noteColor, title }: {
  on: boolean; onChange: (v: boolean) => void; label: string; note?: string; noteColor?: string; title?: string;
}) {
  return (
    <label title={title} className="t-micro" style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4, cursor: 'pointer' }}>
      <input type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} />
      {label}
      {note && <span className="num" style={{ color: noteColor || 'var(--dim2)', fontSize: 10 }}>({note})</span>}
    </label>
  );
}

function Slider({ label, value, max, step, fmt, onChange }: {
  label: string; value: number; max: number; step: number; fmt: (v: number) => string; onChange: (v: number) => void;
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div className="t-label" style={{ marginBottom: 4 }}>
        {label} <span className="num" style={{ color: 'var(--or2)' }}>{fmt(value)}</span>
      </div>
      <input type="range" min={0} max={max} step={step} value={value}
        onChange={(e) => onChange(+e.target.value)} style={{ width: '100%' }} />
    </div>
  );
}

/* The searchable multi-picker. 467 sub-segments in a plain <select> is unusable, which is why every
   one of these has a search box. MULTI-SELECT because "aerospace OR marine, sanding OR grinding" is
   the normal shape of a real GTM question — selected values become removable chips and the list
   stays open after a click, since choosing four things should not mean opening it four times.
   `cap` limits the RESTING list; typing always searches the full set. */
const ACRONYM = new Set(['OEM', 'EV', 'TRL', 'UTV', 'ATV', 'UAS', 'MRO', 'HVAC', 'RV', 'CNC', 'EPC',
  'BESS', 'GT', 'UG', 'AG', 'T1', 'T2', 'PCB', 'LED', 'HVDC', 'AC', 'DC', 'ICE', 'SUV']);
/* A sub-segment code with no row in the taxonomy sheet used to render raw — "SV_EQUIPMENT-TRL"
   reached a reviewer as a bare slug and the question "what is that?" was a fair one. 68 of the 467
   codes the client's tagger emitted are absent from the client's own 400-row sheet. No meaning is
   invented: the code is de-slugged mechanically and shown with a "no definition" mark, so a missing
   definition reads as a gap in the source file rather than a rendering fault here. */
function prettyCode(code: string, l1?: string | null) {
  let s = String(code || '');
  if (l1 && s.toUpperCase().startsWith(String(l1).toUpperCase() + '_')) s = s.slice(String(l1).length + 1);
  const words = s.replace(/[_-]+/g, ' ').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return code;
  return words.map((w) => (ACRONYM.has(w.toUpperCase()) ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1).toLowerCase())).join(' ');
}
const optLabel = (o: GmrOpt) => (o.name ? o.name : prettyCode(o.code, o.l1));

function Combo({ opts, value, onPick, placeholder, cap = 0 }: {
  opts: GmrOpt[]; value: string[]; onPick: (v: string[]) => void; placeholder: string; cap?: number;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const chosen = new Set(value);
  const all = opts.filter((o) => !term || optLabel(o).toLowerCase().includes(term.toLowerCase()) || o.code.toLowerCase().includes(term.toLowerCase()));
  const capped = !term && cap > 0 && all.length > cap;
  const shown = capped ? all.slice(0, cap) : all.slice(0, 400);
  const toggle = (code: string) => onPick(chosen.has(code) ? value.filter((x) => x !== code) : [...value, code]);

  return (
    <div style={{ marginBottom: 6, position: 'relative' }}>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 3 }}>
          {value.map((code) => {
            const o = opts.find((x) => x.code === code);
            return (
              <button key={code} onClick={() => toggle(code)}
                style={{ border: HAIR, background: 'rgba(255,170,0,.043)', color: '#FFF', cursor: 'pointer', padding: '1px 5px', fontSize: 'var(--t-micro)', fontFamily: 'var(--sans)' }}>
                {o ? optLabel(o) : code} ×
              </button>
            );
          })}
        </div>
      )}
      <input
        style={selStyle}
        placeholder={value.length ? 'add another…' : placeholder}
        value={term}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 140)}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); e.currentTarget.blur(); }
          if (e.key === 'Backspace' && !term && value.length) onPick(value.slice(0, -1));
        }}
      />
      {open && (
        <div style={{ maxHeight: 220, overflowY: 'auto', border: HAIR, borderTop: 'none', background: 'var(--s2)' }}>
          {shown.length === 0 && <Hint style={{ padding: 6 }}>nothing matches</Hint>}
          {shown.map((o) => (
            <div
              key={o.code}
              onMouseDown={(e) => { e.preventDefault(); toggle(o.code); setTerm(''); }}
              style={{
                display: 'flex', gap: 6, alignItems: 'baseline', padding: '3px 6px', cursor: 'pointer',
                fontSize: 'var(--t-micro)', color: chosen.has(o.code) ? '#FFF' : 'var(--dim)',
                background: chosen.has(o.code) ? 'rgba(255,170,0,.043)' : undefined,
              }}
            >
              <span style={{ width: 9, color: 'var(--or)' }}>{chosen.has(o.code) ? '✓' : ''}</span>
              <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{optLabel(o)}</span>
              {!o.name && <span title="This code is not in your taxonomy sheet" style={{ color: 'var(--dim2)', fontStyle: 'italic' }}>no definition</span>}
              <span className="num" style={{ color: 'var(--dim2)' }}>{N(o.n)}</span>
            </div>
          ))}
          {capped && <Hint style={{ padding: 6 }}>+ {N(all.length - cap)} rarer — type to find them</Hint>}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────── panes

/* Every number on this screen has to reconcile. 39,743 plants in the header against 14,005 on the
   map, with no way to tell whether either was wrong. Neither was — but a gap you cannot
   account for is indistinguishable from a bug, so the map states its own funnel and the terms add up
   to the dataset total exactly. */
function funnelText(funnel: E.Funnel | null, shown: number, showDark: boolean, trunc: number, shownPts: number) {
  if (!funnel) return '';
  const geo = funnel.total - funnel.ungeocoded;
  const darkHidden = showDark ? 0 : funnel.dark_geo;
  const byFilter = Math.max(0, geo - darkHidden - shown);
  const bits = [`${N(shown)} shown of ${N(funnel.total)} plants`];
  if (funnel.ungeocoded) bits.push(`${N(funnel.ungeocoded)} not geocoded`);
  if (darkHidden) bits.push(`${N(darkHidden)} nobody reachable, hidden`);
  if (byFilter) bits.push(`${N(byFilter)} filtered out`);
  // Server-reported point cap, stated so a capped map never attributes the overflow to filters.
  if (trunc && trunc > shownPts) bits.push(`showing ${N(shownPts)} of ${N(trunc)} points — the rest are past the map's cap`);
  return bits.join(' · ');
}

/* The panel is never blank: with no plant selected it is the ranked worklist for whatever the map
   currently shows, which is also the answer to "where do we sell next". */
function Worklist({ rows, funnel, show, trunc, shownPts, onPick }: {
  rows: E.MapRow[]; funnel: E.Funnel | null; show: string; trunc: number; shownPts: number; onPick: (id: number) => void;
}) {
  const top = rows.slice().sort((a, b) => b.reach - a.reach || (b.sqft || 0) - (a.sqft || 0)).slice(0, 60);
  return (
    <div>
      <div style={{ padding: 'var(--gut)', borderBottom: HAIR }}>
        <div className="t-label">Worklist for the current map</div>
        <div className="t-disp" style={{ marginTop: 2 }}>{N(rows.length)}</div>
        <Hint style={{ marginTop: 4 }}>plants in view · {funnelText(funnel, rows.length, show === 'all', trunc, shownPts)}</Hint>
      </div>
      <Hint style={{ padding: 'var(--gut)' }}>
        Click any plant on the map, or start here. Shift-drag a box on the map to turn a region into a trip.
      </Hint>
      <PlantRows rows={top} onPick={onPick} />
    </div>
  );
}

function RegionPanel({ rows, onPick }: { rows: E.MapRow[]; onPick: (id: number) => void }) {
  const sq = rows.reduce((a, c) => a + (c.sqft || 0), 0);
  const rc = rows.reduce((a, c) => a + c.reach, 0);
  return (
    <div>
      <div style={{ padding: 'var(--gut)', borderBottom: HAIR }}>
        <div className="t-disp">{N(rows.length)}</div>
        <Hint>plants · {SQ(sq)} sq ft · {N(rc)} reachable buyers · {rows.filter((x) => x.committee === 4).length} with a full committee</Hint>
      </div>
      <Hint style={{ padding: 'var(--gut)' }}>
        A trip, not a list. Sorted by reachable buyers — the working order for two days in this region.
      </Hint>
      <PlantRows rows={rows.slice().sort((a, b) => b.reach - a.reach).slice(0, 80)} onPick={onPick} />
    </div>
  );
}

/* A column header the list never had, and a four-segment meter instead of a coloured pill: every
   pill was the same shape, so committee coverage could only be read by parsing the digits on each
   row. The meter reads DOWN the column in one pass, which is the point of a worklist. */
function PlantRows({ rows, onPick }: { rows: E.MapRow[]; onPick: (id: number) => void }) {
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, padding: '4px var(--gut)', borderBottom: HAIR, position: 'sticky', top: 0, background: 'var(--s3)', zIndex: 1 }}>
        <span className="t-label" style={{ flex: '1 1 auto', minWidth: 0 }}>Site</span>
        <span className="t-label" style={{ width: 52, textAlign: 'right' }}>Sq ft</span>
        <span className="t-label" style={{ width: 42 }}>Roles</span>
        <span className="t-label" style={{ width: 40, textAlign: 'right' }}>Reach</span>
      </div>
      {rows.map((r) => (
        <div key={r.id} onClick={() => onPick(r.id)}
          style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px var(--gut)', borderBottom: HAIR, cursor: 'pointer' }}>
          <span style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.site || r.company}</div>
            <div className="t-micro" style={{ color: 'var(--dim2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.city || ''}, {r.state || ''} · {r.company}
            </div>
          </span>
          <span className="num" style={{ width: 52, color: 'var(--dim)' }}>{SQ(r.sqft)}</span>
          <span style={{ width: 42 }}><Meter n={r.committee} /></span>
          <span className="num" style={{ width: 40, color: r.reach ? 'var(--am)' : 'var(--dim2)' }}>{N(r.reach)}</span>
        </div>
      ))}
    </div>
  );
}

interface OrgHead {
  sqft: number | null; tier: string | null; placed: number; reach: number; committee: number;
  avg_mi: number | null; dm: number; sp: number; inf: number; usr: number;
  site: string | null; company: string | null; city: string | null; state: string | null; domain: string | null;
}

/* The four buying roles, in GMR's own terms. The stored icp_role is unchanged — see ROLE_LABEL in
   lib/fmt.ts for why the rename is a label and not a data change. */
const ROLE_TILES: [key: keyof OrgHead, role: string][] = [
  ['dm', 'decision_maker'], ['sp', 'sponsor'], ['inf', 'influencer'], ['usr', 'user'],
];

function PlantPanel({ id, radius, onOpenPerson }: { id: number; radius: number; onOpenPerson?: (pid: number) => void }) {
  const [d, setD] = useState<{ head: OrgHead; cells: Cell[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /* ONE place decides what the people list is showing, so the label under "People" can never
     disagree with the rows underneath it. */
  const [scope, setScope] = useState<Record<string, string | number>>({});
  const [what, setWhat] = useState('everyone reachable here');
  const [term, setTerm] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const [lvl, setLvl] = useState<string>('all');

  useEffect(() => {
    let dead = false;
    setD(null); setErr(null); setScope({}); setWhat('everyone reachable here'); setRole(null); setLvl('all'); setTerm('');
    api<{ head: OrgHead; cells: Cell[] }>('org?scope=fac&key=' + id).then((r) => {
      if (dead) return;
      if (isErr(r)) setErr(r.error); else setD(r);
    });
    return () => { dead = true; };
  }, [id]);

  const row = E.MAP.byId[id];
  if (err) return <ErrBox what="This plant did not load" msg={err} />;
  if (!d) return <Hint style={{ padding: 'var(--gut)' }}>loading…</Hint>;
  const hd = d.head;

  const near = radius && row
    ? E.MAP.rows.filter((r) => haversine(row.lat, row.lng, r.lat, r.lng) <= radius)
    : null;

  const params: Record<string, string | number> = { fac: id, ...scope, ...(term ? { q: term } : {}) };

  return (
    <div>
      <div style={{ padding: 'var(--gut)', borderBottom: HAIR }}>
        <div style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>{hd.site || 'plant ' + id}</div>
        <Hint>{hd.company || ''} · {hd.city || ''}, {hd.state || ''} · <span style={{ fontFamily: 'var(--mono)', color: 'var(--bl)' }}>{hd.domain || ''}</span></Hint>
        {near && (
          <Hint style={{ marginTop: 5, color: 'var(--or2)' }}>
            {radius} mi radius: {near.length} plants, {N(near.reduce((a, c) => a + c.reach, 0))} reachable buyers, {SQ(near.reduce((a, c) => a + (c.sqft || 0), 0))} sq ft.
          </Hint>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: HAIR }}>
        <Stat label="Sq ft" value={SQ(hd.sqft)} />
        <Stat label="Tier" value={'T' + (hd.tier || '?')} />
        <Stat label="Placed here" value={N(hd.placed)} title={PLACED_NOTE} />
        <Stat label="Reachable" value={N(hd.reach)} />
        <Stat label="Committee" value={`${hd.committee || 0}/4`} />
        <Stat label="Avg miles" value={N1(hd.avg_mi)} />
      </div>

      <Section label="Buying committee at this building" right={<Hint>click a role to see those people</Hint>}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {ROLE_TILES.map(([key, r]) => {
            const label = ROLE_LABEL[r];
            const n = Number(hd[key] || 0);
            const on = role === r;
            return (
              <Btn key={r} sm={false} on={on} disabled={!n}
                title={`${ROLE_MEANS[r]}

${n ? `Show the ${n} reachable at this plant.`
                  : 'Nobody in this role is reachable at this building. That is the gap, not a filter.'}`}
                onClick={() => {
                  if (on) { setRole(null); setScope({}); setWhat('everyone reachable here'); return; }
                  setRole(r); setScope({ role: r }); setWhat(`${label} at this plant`);
                }}
                style={{ minWidth: 96 }}>
                <div className="t-label">{label}</div>
                <div className="t-fig" style={{ color: n ? 'var(--ok)' : 'var(--rd)' }}>{n || 'none'}</div>
                <div className="t-micro" style={{ color: 'var(--dim2)' }}>{n ? 'reachable · click' : 'gap at this plant'}</div>
              </Btn>
            );
          })}
        </div>
      </Section>

      <Section label="Org lattice at this plant" right={<Hint>click a cell for those people</Hint>}>
        <Matrix cells={d.cells} onCell={(dept, level) => {
          setRole(null);
          setScope({ dept, level });
          setWhat(`${dept} · ${LVLNAME[level] || 'level ' + level}`);
        }} />
      </Section>

      <Section label="People" right={<Hint>{what}</Hint>}>
        <input style={selStyle} placeholder="filter by name or job title…" value={term}
          onChange={(e) => setTerm(e.target.value)} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 6 }}>
          {([['all', 'all levels'], ['6', 'VP+'], ['4', 'Manager+'], ['3-5', 'Line leadership 3-5'], ['0-2', 'Floor 0-2']] as const).map(([k, l]) => (
            <Btn key={k} sm on={lvl === k} onClick={() => {
              setLvl(k);
              /* "all levels" restores the unfiltered default, so it sends NO level bounds at all. It
                 used to send lmin="0", and the server treats any present lmin — "0" included — as a
                 filter, which silently dropped the No-title (-1) and Unmapped (-9) people the
                 initial view includes. */
              const { lmin: _a, lmax: _b, ...rest } = scope;
              if (k === 'all') { setScope(rest); return; }
              const [min, max] = k.includes('-') ? k.split('-') : [k, '8'];
              setScope({ ...rest, lmin: min, lmax: max });
            }}>{l}</Btn>
          ))}
        </div>
      </Section>
      <PeopleList params={params} onPick={onOpenPerson} />
    </div>
  );
}

